import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* A refusal is a real HTTP status carrying { code, message } — there is no
   status field in the body to test. The text is what the screens render, so
   the code is only the fallback when a route answers without a sentence. */
const apiError = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

/* ============================
   FETCH ALL CATEGORIES
   GET /admin/catalog/categories answers { data, pagination } — the inner
   `data` is the payload's own key, not a leftover envelope. Rows carry
   id (the legacy mongo id), name, slug, imageUrl, sortOrder, isActive,
   productCount and subcategoryCount.
============================ */
export const fetchCategories = createAsyncThunk(
  "category/fetchCategories",
  async ({ page = 1, limit = 10, search = "" }, thunkAPI) => {
    try {
      const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) qs.set("search", search);
      const res = await api.get(`/admin/catalog/categories?${qs.toString()}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to fetch categories"));
    }
  }
);
/* ============================
   FETCH DROPDOWN
   { data: [{ id, name, image }] } — active aisles only, Millables excluded.
============================ */
export const fetchCategoryDropdown = createAsyncThunk(
  "category/fetchDropdown",
  async (_, thunkAPI) => {
    try {
      const res = await api.get(`/admin/catalog/categories/dropdown`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to fetch dropdown"));
    }
  }
);

/* ============================
   CREATE CATEGORY
   JSON, not multipart: the service binds a body of { name, image | imageUrl,
   sortOrder, status | isActive } and has no image upload endpoint, so the
   image travels as the URL string the form already holds. It answers 201
   { message, data: <row> }, and 409 { code: "duplicate" } for a name that
   already exists, case-folded.
============================ */
export const createCategory = createAsyncThunk(
  "category/createCategory",
  async (body, thunkAPI) => {
    try {
      const res = await api.post(`/admin/catalog/categories`, body);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to create category"));
    }
  }
);

/* ============================
   UPDATE CATEGORY
   { message, data: <row> }. An absent image key leaves the stored one alone.
   `formData` keeps its name for the callers, but it is a plain JSON object
   now — a FormData instance is a 415 against this service.
============================ */
export const updateCategory = createAsyncThunk(
  "category/updateCategory",
  async ({ id, formData }, thunkAPI) => {
    try {
      const res = await api.put(`/admin/catalog/categories/${id}`, formData);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to update category"));
    }
  }
);

/* ============================
   TOGGLE STATUS
   No body — it flips the stored flag and answers
   { message, data: { id, name, isActive } }.
============================ */
export const toggleCategoryStatus = createAsyncThunk(
  "category/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/admin/catalog/categories/${id}/status`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to update status"));
    }
  }
);

/* ============================
   DELETE CATEGORY
   Superadmin only. It refuses with a sentence rather than orphaning rows:
   400 has_subcategories, 409 in_use, 400 system_managed for Millables.
============================ */
export const deleteCategory = createAsyncThunk(
  "category/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/admin/catalog/categories/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Delete failed"));
    }
  }
);

/* ============================
   SLICE
============================ */
const categorySlice = createSlice({
  name: "category",
  initialState: {
    categories: [],
    dropdownCategories: [],
    loading: false,
    error: null,
    pagination: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Dropdown
      .addCase(fetchCategoryDropdown.fulfilled, (state, action) => {
        state.dropdownCategories = action.payload;
      })

      // Create
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories.unshift(action.payload);
        state.pagination.total = (state.pagination.total || 0) + 1;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.error = action.payload;
      })

      /* Update — a category is addressed by `id`; the service returns no `_id`.
         The PUT answers { id, name, slug, imageUrl, sortOrder, isActive,
         updatedAt } and carries neither productCount nor subcategoryCount, so
         the row is merged rather than replaced: swapping it in wholesale
         emptied both count columns for the row just saved. */
      .addCase(updateCategory.fulfilled, (state, action) => {
        const index = state.categories.findIndex(
          (c) => c.id === action.payload.id
        );
        if (index !== -1) Object.assign(state.categories[index], action.payload);
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Toggle Status — the answer is { id, name, isActive } only, so the flag
      // is merged rather than swapped in over a fuller row
      .addCase(toggleCategoryStatus.fulfilled, (state, action) => {
        const index = state.categories.findIndex(
          (c) => c.id === action.payload.id
        );
        if (index !== -1) state.categories[index].isActive = action.payload.isActive;
      })
      /* The toggle is dispatched without unwrap, and 403 (the taxonomy is
         estate-wide) is a real status now rather than a 200 with a body field,
         so the refusal is recorded here or it is lost. */
      .addCase(toggleCategoryStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(
          (c) => c.id !== action.payload
        );
        state.pagination.total = (state.pagination.total || 1) - 1;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default categorySlice.reducer;